/**
 * Formats a number as a price with commas.
 *
 * @param price - The number to format.
 * @param currencySymbol - Optional. The currency symbol to prepend (e.g., '$', 'PKR', '€').
 * @param locale - Optional. The locale for formatting (default: 'en-US').
 * @returns A string representing the formatted price.
 */
import { Address } from "src/graphql/quark/quark.type"
import { isValidPhoneNumber } from "./phone-utils"

const isPhoneValid = (phone: string) => {
  return isValidPhoneNumber(phone)
}

const getDisplayPrice = (price: number, currencySymbol?: string, locale?: string): string => {
  if (isNaN(price)) return "Invalid price"

  // Format the number with commas based on the locale
  const formattedPrice = price.toLocaleString(locale ?? "en-US", { maximumFractionDigits: 2 })

  // Add the currency symbol if provided
  return currencySymbol ? `${currencySymbol}${formattedPrice}` : formattedPrice
}

// export const debounce = (func: (...args: any[]) => void, wait: number) => {
//   let timeout: NodeJS.Timeout
//   return (...args: any[]) => {
//     clearTimeout(timeout)
//     timeout = setTimeout(() => func(...args), wait)
//   }
// }

const formatAddress = (address: Address): string => {
  const parts = [
    address.buildingNo,
    address.streetAddress1,
    address.streetAddress2,
    address.city?.name,
    address.city?.country?.name,
    address.state,
    address.postalCode
  ]

  return parts.filter((part) => part && part.trim() !== "").join(", ")
}

const debounce = ({
  value,
  getValue,
  timeOut = 500
}: {
  value: any
  getValue: (val: any) => void
  timeOut: number
}) => {
  const localStorageKey = "timoutId"
  clearTimeout(Number(localStorage.getItem(localStorageKey)))
  localStorage.removeItem(localStorageKey)
  localStorage.setItem(
    localStorageKey,
    String(
      setTimeout(() => {
        getValue(value)
      }, timeOut)
    )
  )
}

const stringToColor = (string: string) => {
  let hash = 0
  let i

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = "#"

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${value.toString(16)}`.slice(-2)
  }
  /* eslint-enable no-bitwise */

  return color
}

// Keeps a left-to-right value (an ID, a date, an amount) in one piece inside a translated
// sentence. In Arabic and Urdu, "963811-001" otherwise shows as "001-963811".
const ltr = (value: string | number): string => `\u2066${value}\u2069`

export { stringToColor, debounce, getDisplayPrice, isPhoneValid, formatAddress, ltr }
